import Vue from 'vue'
import environment from '../config/environment.js'

// let jws = require('jws')

const REVERSE_GEOCODING_CACHE_TTL_MS = 24 * 60 * 60 * 1000
const REVERSE_GEOCODING_CACHE_MAX_ENTRIES = 2000
// Nominatim zoom level that resolves to a city/town rather than the street the
// vehicle is standing on.
const CITY_ZOOM = 10

const reverseGeocodingCache = new Map()
const reverseGeocodingPending = new Map()

function getReverseCoordinates(options) {
    const lat = Number(options?.lat)
    const lng = Number(options?.lng)

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null

    return {
        lat: lat.toFixed(6),
        lng: lng.toFixed(6)
    }
}

function getReverseCacheKey(options) {
    const coordinates = getReverseCoordinates(options)
    if (!coordinates) return null

    // A city-level lookup is a different answer for the same point
    const suffix = options.city_level ? `@${CITY_ZOOM}` : ''

    return `${coordinates.lat},${coordinates.lng}${suffix}`
}

function getCachedReverseResponse(key) {
    const cached = reverseGeocodingCache.get(key)

    if (!cached) return null

    if (Date.now() - cached.cachedAt > REVERSE_GEOCODING_CACHE_TTL_MS) {
        reverseGeocodingCache.delete(key)
        return null
    }

    // Refresh insertion order so the Map behaves as an LRU cache.
    reverseGeocodingCache.delete(key)
    reverseGeocodingCache.set(key, cached)

    return cached.response
}

function cacheReverseResponse(key, response) {
    reverseGeocodingCache.set(key, { response, cachedAt: Date.now() })

    while (reverseGeocodingCache.size > REVERSE_GEOCODING_CACHE_MAX_ENTRIES) {
        reverseGeocodingCache.delete(reverseGeocodingCache.keys().next().value)
    }
}

async function fetchReverseResponse(options) {
    const key = getReverseCacheKey(options)
    if (!key) return null
    const cached = getCachedReverseResponse(key)

    if (cached) return cached
    if (reverseGeocodingPending.has(key)) return reverseGeocodingPending.get(key)
    if (!environment.REVERSE_GEOCODING_AUTHORIZATION) {
        throw new Error(
            'Reverse geocoding is not configured: set VUE_APP_PTV_PASSWORD or VUE_APP_REVERSE_GEOCODING_AUTHORIZATION'
        )
    }

    const reverseUrl = new URL(environment.REVERSE_GEOCODING_URL)
    reverseUrl.searchParams.set('lat', Number(options.lat).toFixed(6))
    reverseUrl.searchParams.set('lon', Number(options.lng).toFixed(6))
    reverseUrl.searchParams.set('format', 'json')
    if (options.city_level) reverseUrl.searchParams.set('zoom', String(CITY_ZOOM))

    const headers = new Headers({
        Authorization: environment.REVERSE_GEOCODING_AUTHORIZATION
    })

    const request = fetch(
        reverseUrl.toString(),
        {
            headers,
            cache: 'force-cache'
        }
    )
        .then((response) => {
            if (!response.ok) throw new Error(`Reverse geocoding failed: ${response.status}`)
            return response.json()
        })
        .then((response) => {
            cacheReverseResponse(key, response)
            return response
        })
        .finally(() => reverseGeocodingPending.delete(key))

    reverseGeocodingPending.set(key, request)
    return request
}

function formatReverseResponse(response, options, vueInstance) {
    if (!response?.address) return null

    if (options.raw_address_object) return response.address

    if (options.full) return response.display_name

    // Nearest city: the first `custom_fields` entry the city-level lookup has
    // an answer for, and nothing else.
    if (options.city_level) {
        return (
            (options.custom_fields || [])
                .map((field) => response.address[field])
                .find((value) => !!value) || null
        )
    }

    if (options.custom_fields) {
        return options.custom_fields
            .map((field) => {
                const value = response.address[field]
                if (field === 'country_code' && value) return String(value).toUpperCase()
                return value
            })
            .filter((value) => !!value)
            .join(', ')
    }

    const configuredComposition =
        options.addressComposition ||
        vueInstance?.$store?.state?.config?.user?.addressComposition ||
        null

    const defaultComposition = [
        'road',
        'village',
        'town',
        'county',
        'city',
        'state',
        'country'
    ]

    const addressComposition =
        Array.isArray(configuredComposition) && configuredComposition.length > 0
            ? configuredComposition
            : defaultComposition

    const normalizedComposition = addressComposition
        .map((field) => {
            if (typeof field === 'string') return field
            if (field && typeof field === 'object') return field.id || field.value || null
            return null
        })
        .filter((field) => !!field)

    let address = normalizedComposition
        .map((field) => {
            if (field === 'city' && response.address.city == response.address.county) return null
            const value = response.address[field]
            if (field === 'country_code' && value) return String(value).toUpperCase()
            return value
        })
        .filter((value) => !!value)

    address = address.filter((value, index, values) => values.indexOf(value) === index)

    return options.raw ? address : address.join(', ')
}

const golia = {
    install: function (Vue) {
        Vue.prototype.$getCachedReverseGeocoding = function (options) {
            const key = getReverseCacheKey(options)
            if (!key) return null

            const response = getCachedReverseResponse(key)
            return response ? formatReverseResponse(response, options, this) : null
        }

        Vue.prototype.$reverseGeocoding = async function (options) {
            try {
                const response = await fetchReverseResponse(options)
                return formatReverseResponse(response, options, this)
            } catch (e) {
                console.error(e)
            }
        }

        // Apre una pagina del gestionale Golia (GOLIA_CORE_URL) passando un
        // payload che viene serializzato in JSON e codificato in base64.
        Vue.prototype.$openGoliaCore = function (payload) {
            const encoded = btoa(JSON.stringify(payload))

            window.open(this.$env.GOLIA_CORE_URL + encoded, '_blank', 'noreferrer')
        }

        Vue.prototype.$path_join = function (...args) {
            var splitted = args.join('/').split(/http(s|):\/\//g)

            var path = splitted.pop().replace(/\/+/g, '/')

            return (splitted.length > 1 ? 'http' + splitted[1] + '://' : '') + path
        }

        Vue.filter('truncate', function (text, length, clamp) {
            clamp = clamp || ''

            return text.length > length ? text.slice(0, length) + clamp : text
        })

        Vue.filter('currency', function (value, currency) {
            currency = currency || 'EUR'
            let lang_code = document.querySelector('html').getAttribute('lang') || 'it-IT'

            return new Intl.NumberFormat(lang_code, {
                style: 'currency',
                currency: currency
            }).format(value)
        })

        //HELPERS
        Vue.prototype.$isBrowserWindowActive = (() => {
            if (document.visibilityState == 'undefined') document.visibilityState = 'visible'

            return () => {
                return document.visibilityState == 'visible'
            }
        })()

        //DIRECTIVES

        Vue.directive('click-outside', {
            bind: function (el, binding) {
                el.clickOutsideEvent = function (event) {
                    if (!(el == event.target || el.contains(event.target))) binding.value(el)
                }
                document.body.addEventListener('click', el.clickOutsideEvent)
            },
            unbind: function (el) {
                document.body.removeEventListener('click', el.clickOutsideEvent)
            }
        })

        Vue.directive('resized', {
            inserted: function (el, binding, vnode) {
                if (!ResizeObserver) return false

                el.resize_observer = {}

                el.resize_observer.obj = new ResizeObserver(
                    ((el) => {
                        return (ent) => {
                            el.resize_observer.rect = ent[0].contentRect

                            if (el.resize_observer.timer) return false

                            el.resize_observer.timer = setTimeout(() => {
                                binding.value.bind(vnode.context)(el.resize_observer.rect)

                                clearTimeout(el.resize_observer.timer)

                                el.resize_observer.timer = null
                            }, 300)
                        }
                    })(el)
                ).observe(el)
            },
            unbind: function (el) {
                if (el.resize_observer.obj) el.resize_observer.obj.unobserve(el)
            }
        })
    }
}

export default golia
